import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  NativeSyntheticEvent,
  TextInputChangeEventData,
  View,
} from 'react-native'
import Button from '@ui/buttons/Button'
import { VerifyContentProps } from './types'
import { useSubmitAuthCode } from './useSubmitAuthCode'
import Input from '@ui/forms/Input'
import CodeScannerInput from '@/common/CodeScanner/CodeScannerInput'
import { useQrScanner } from '@/common/CodeScanner/useQrScanner'
import { useDateFormat } from '@ui/hooks/useDateFormat'
import { useStaff } from 'hooks/useSession'

export default function StaffAuthContent({
  verifyRes,
  customer,
  replace = true,
  onSuccess,
}: VerifyContentProps) {
  const { t } = useTranslation(['auth', 'common'])
  const { mode, setMode } = useQrScanner()
  const [code, setCode] = useState('')
  const [invalid, setInvalid] = useState(false)

  const formatDate = useDateFormat({ dateStyle: 'medium' })

  const staff = useStaff()
  const staffName = staff?.name || t('common:staff')

  useEffect(() => {
    setMode('dynamic')
    return () => {
      setMode('disabled')
    }
  }, [mode, setMode])

  const { onSubmit, isLoading } = useSubmitAuthCode({
    login: verifyRes?.login || '',
    strategy: verifyRes?.strategy || 'staff',
    id: customer?.id,
    replace,
    onSuccess,
    onError: () => setInvalid(true),
  })

  const onChange = (event: NativeSyntheticEvent<TextInputChangeEventData>) => {
    if (isLoading) return
    setCode(event.nativeEvent.text)
    setInvalid(false)
  }

  const city = [customer?.address?.zip, customer?.address?.city]
    .filter((v) => !!v)
    .join(' ')

  const street = [customer?.address?.street, customer?.address?.street2]
    .filter((v) => !!v)
    .join(' ')

  const submit = () => onSubmit(code)

  const onScan = useCallback(
    (result: string) => {
      setCode(result)
      onSubmit(result)
    },
    [setCode, onSubmit]
  )

  return (
    <View className="w-full items-center gap-16">
      <View className="w-full flex-1 gap-4">
        <View className="flex w-full flex-row gap-4">
          <Input
            disabled
            value={customer?.first_name + ' ' + customer?.last_name}
            className="flex-1"
          />
          <Input
            disabled
            value={formatDate(customer?.birth_date)}
            className="flex-1"
          />
        </View>
        <Input disabled value={`${street}, ${city}`} />
        <Input
          placeholder={`${t('auth:staffId.label')} ${`• ${staffName}`}`}
          textEnd={code ? staffName : undefined}
          value={code}
          onChange={onChange}
          onSubmitEditing={submit}
          action={<CodeScannerInput onSubmit={onScan} />}
          errorMessage={invalid ? t(`validation:staffId.invalid`) : undefined}
          keyboardAppearance="dark"
        />
      </View>
      <Button
        onPress={submit}
        label={t('auth:signIn')}
        loading={isLoading}
        iconEnd="arrow-narrow-right"
      />
    </View>
  )
}
